/**
 * Front end: main menu, pause menu and the settings panel.
 *
 * The settings panel is the team's tuning tool, so every control is wired to a
 * real system and feature-detected — a control whose backing API has not landed
 * yet still stores its value and re-applies the moment the API appears.
 *
 * Pointer lock stays owned by src/core/Input.js. We only ever ask to release it
 * and disable input while a menu is up; re-locking happens through Input's own
 * canvas mousedown handler.
 */
const QUALITY = ['potato', 'low', 'medium', 'high', 'ultra'];

/** id -> [ctx.config key, optional ctx.render setter, optional value mapper] */
const FORWARD = {
  fpsLimit: ['fpsLimit', 'setFrameLimit'],
  shadowQuality: ['shadowQuality', 'setShadowQuality', (v) => ['off', 'low', 'high', 'ultra'][v]],
  shadowDistance: ['shadowDistance', 'setShadowDistance'],
  lodBias: ['lodBias', 'setLodBias'],
  aniso: ['anisotropy', 'setAnisotropy', (v) => [1, 4, 8, 16][v]],
  textureQuality: ['textureQuality', 'setTextureQuality', (v) => ['low', 'medium', 'high', 'ultra'][v]],
  gamma: ['gamma', 'setGamma'],
  sharpen: ['sharpen', 'setSharpen'],
};

/** Fallback pass list used until ctx.render.passes exists. */
const FALLBACK_PASSES = [
  ['taa', 'Temporal AA'], ['ssao', 'Ambient Occlusion'], ['ssr', 'Screen-Space Reflections'],
  ['bloom', 'Bloom'], ['dof', 'Depth of Field'], ['motionBlur', 'Motion Blur'],
  ['chromatic', 'Chromatic Aberration'], ['grain', 'Film Grain'], ['vignette', 'Vignette'],
];

export class Menus {
  constructor(hud) {
    this.hud = hud;
    this.ctx = null;
    this.mode = null;              // null | 'main' | 'pause' | 'settings'
    this.prevMode = null;
    this.tab = 'gameplay';
    this.values = {};
    this.rows = new Map();
    this._descEl = null;
    this._onKey = null;
  }

  /* ------------------------------------------------------------- lifecycle */

  init(ctx, layer) {
    this.ctx = ctx;
    this.layer = layer;
    this._readDefaults();
    this._build();
    this._onKey = (e) => this._key(e);
    window.addEventListener('keydown', this._onKey, true);
  }

  dispose() {
    if (this._onKey) window.removeEventListener('keydown', this._onKey, true);
    this.layer?.replaceChildren();
  }

  _key(e) {
    if (e.code === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      if (this.hud?.intro?.visible) { this.hud.intro.hide(); return; }
      if (this.mode === 'settings') this.open(this.prevMode || 'pause');
      else if (this.mode) this.close();
      else this.open('pause');
    }
  }

  open(mode) {
    if (mode === 'settings' && this.mode && this.mode !== 'settings') this.prevMode = this.mode;
    this.mode = mode;
    this._sync();
    this.layer.classList.add('open');
    this.layer.classList.toggle('mode-pause', mode === 'pause');
    for (const [k, el] of Object.entries(this.panels)) el.classList.toggle('show', k === mode);
    const input = this.ctx?.input;
    if (input) input.enabled = false;
    if (document.pointerLockElement) document.exitPointerLock?.();
    this.hud?.onMenuChange?.(mode);
  }

  close() {
    this.mode = null;
    this.layer.classList.remove('open', 'mode-pause');
    for (const el of Object.values(this.panels)) el.classList.remove('show');
    const input = this.ctx?.input;
    if (input) input.enabled = true;
    this.hud?.onMenuChange?.(null);
  }

  get isOpen() { return this.mode !== null; }

  /* -------------------------------------------------------------- values */

  _readDefaults() {
    const ctx = this.ctx;
    const input = ctx?.input;
    const cam = ctx?.camera;
    const r = this._render();
    this.values = {
      fov: cam?.fov ?? 80,
      adsFov: ctx?.config?.adsFovScale ?? 0.78,
      sens: input?.sensitivity ?? 0.0022,
      adsSens: input?.adsSensitivityScale ?? 0.72,
      invertY: !!input?.invertY,
      quality: QUALITY.indexOf(ctx?.quality ?? 'ultra'),
      exposure: ctx?.renderer?.toneMappingExposure ?? 1,
      renderScale: ctx?.config?.renderScale ?? 1,
      fpsLimit: ctx?.config?.fpsLimit ?? 240,
      shadowQuality: 3,
      shadowDistance: ctx?.config?.shadowDistance ?? 90,
      lodBias: ctx?.config?.lodBias ?? 0,
      aniso: 3,
      textureQuality: 3,
      gamma: ctx?.config?.gamma ?? 1,
      sharpen: ctx?.config?.sharpen ?? 0.3,
      crosshairDot: true,
      crosshairOpacity: 1,
      crosshairThickness: 2,
      hudOpacity: 1,
      hudScale: 1,
      compass: true,
      minimap: true,
      killfeed: true,
      hitMarkers: true,
      damageIndicators: true,
      worldMarkers: true,
    };
    if (this.values.quality < 0) this.values.quality = QUALITY.length - 1;
    for (const [id] of this._passList()) this.values['pass_' + id] = this._passEnabled(id);
    // Snapshot of what the engine booted with — `resetDefaults` restores THIS,
    // not whatever the live systems happen to hold once the user has been
    // dragging sliders around.
    if (!this._defaults) this._defaults = { ...this.values };
  }

  _render() {
    const ctx = this.ctx;
    return ctx?.render || ctx?.get?.('render') || null;
  }

  _passList() {
    const r = this._render();
    const p = r?.passes;
    if (Array.isArray(p) && p.length) {
      return p.map((x, i) => [x?.name ?? x?.id ?? String(i), pretty(x?.name ?? x?.id ?? String(i))]);
    }
    if (p && typeof p === 'object') {
      const keys = Object.keys(p);
      if (keys.length) return keys.map((k) => [k, pretty(k)]);
    }
    return FALLBACK_PASSES;
  }

  _passEnabled(id) {
    const p = this._render()?.passes;
    if (Array.isArray(p)) {
      const f = p.find((x) => (x?.name ?? x?.id) === id);
      return f ? f.enabled !== false : true;
    }
    if (p && typeof p === 'object' && id in p) {
      const v = p[id];
      return typeof v === 'boolean' ? v : v?.enabled !== false;
    }
    return true;
  }

  /** Push a value into whichever real system owns it. Silently tolerant. */
  apply(id, v) {
    this.values[id] = v;
    const ctx = this.ctx;
    const input = ctx?.input;
    const r = this._render();
    try {
      switch (id) {
        case 'fov':
          if (ctx?.camera) { ctx.camera.fov = v; ctx.camera.updateProjectionMatrix(); }
          if (ctx?.config) ctx.config.fov = v;
          break;
        case 'adsFov': if (ctx?.config) ctx.config.adsFovScale = v; break;
        case 'sens': if (input) input.sensitivity = v; if (ctx?.config) ctx.config.sensitivity = v; break;
        case 'adsSens': if (input) input.adsSensitivityScale = v; break;
        case 'invertY': if (input) input.invertY = v; if (ctx?.config) ctx.config.invertY = v; break;
        case 'quality': {
          const preset = QUALITY[v] || 'ultra';
          if (ctx) ctx.quality = preset;
          r?.setQuality?.(preset);
          break;
        }
        case 'exposure':
          if (r?.setExposure) r.setExposure(v);
          else if (ctx?.renderer) ctx.renderer.toneMappingExposure = v;
          break;
        case 'renderScale':
          if (ctx?.config) ctx.config.renderScale = v;
          r?.setRenderScale?.(v);
          break;
        default:
          if (id.startsWith('pass_')) this._setPass(id.slice(5), v);
          else if (id in FORWARD) {
            // Values the render team has not exposed a setter for yet still land
            // on ctx.config and go out on `ui:setting`, so wiring them up later
            // is a one-line change on their side.
            const [key, setter, map] = FORWARD[id];
            const out = map ? map(v) : v;
            if (ctx?.config) ctx.config[key] = out;
            if (setter && typeof r?.[setter] === 'function') r[setter](out);
          } else this.hud?.applyHudSetting?.(id, v);
      }
      ctx?.events?.emit('ui:setting', { id, value: v });
    } catch (err) {
      console.warn('[ui] setting failed:', id, err);
    }
    this._syncRow(id);
  }

  _setPass(id, on) {
    const p = this._render()?.passes;
    if (Array.isArray(p)) {
      const f = p.find((x) => (x?.name ?? x?.id) === id);
      if (f) f.enabled = on;
    } else if (p && typeof p === 'object' && id in p) {
      if (typeof p[id] === 'boolean') p[id] = on;
      else if (p[id]) p[id].enabled = on;
    }
    this._render()?.setPassEnabled?.(id, on);
  }

  /* --------------------------------------------------------------- build */

  _build() {
    const layer = this.layer;
    layer.replaceChildren();

    const scrim = el('div', 'menu-scrim');
    layer.appendChild(scrim);

    this.panels = {
      main: this._buildMain(),
      pause: this._buildPause(),
      settings: this._buildSettings(),
    };
    for (const p of Object.values(this.panels)) layer.appendChild(p);
  }

  _buildMain() {
    const p = el('div', 'menu-panel menu-main');
    const brand = el('div', 'mm-brand');
    brand.appendChild(el('div', 'mm-eyebrow', 'TASK FORCE 141  //  CLASSIFIED'));
    const t = el('h1', 'mm-title');
    t.innerHTML = '<span>OPERATION</span><em>BLACKOUT</em>';
    brand.appendChild(t);
    brand.appendChild(el('div', 'mm-rule'));
    brand.appendChild(el('div', 'mm-sub', 'MISSION 04 — VERDANSK OUTSKIRTS · 03:14 LOCAL'));
    p.appendChild(brand);

    const nav = el('div', 'mm-nav');
    const items = [
      ['CAMPAIGN', 'RESUME MISSION 04', () => this.close()],
      ['MULTIPLAYER', '12 LOBBIES · 34 MS', () => this.close()],
      ['LOADOUT', 'M4A1 · CQB SPEC', () => this.close()],
      ['SETTINGS', 'CONTROLS · VIDEO · HUD', () => this.open('settings')],
      ['INFO', 'CONTROLS · ABOUT · CREDITS', () => { this.close(); this.hud?.intro?.show?.(); }],
      ['QUIT', 'RETURN TO DESKTOP', () => this.close()],
    ];
    items.forEach(([name, sub, fn], i) => {
      const it = el('button', 'mm-item' + (i === 0 ? ' active' : ''));
      it.appendChild(el('span', 'mm-bar'));
      const col = el('span', 'mm-item-text');
      col.appendChild(el('span', 'mm-item-name', name));
      col.appendChild(el('span', 'mm-item-sub', sub));
      it.appendChild(col);
      it.appendChild(el('span', 'mm-item-idx', String(i + 1).padStart(2, '0')));
      it.addEventListener('click', fn);
      it.addEventListener('mouseenter', () => {
        nav.querySelectorAll('.mm-item').forEach((n) => n.classList.remove('active'));
        it.classList.add('active');
      });
      nav.appendChild(it);
    });
    p.appendChild(nav);

    const stats = el('div', 'mm-stats');
    for (const [k, v, sub] of [
      ['TIME PLAYED', '38:12', 'HOURS THIS SEASON'],
      ['ACCURACY', '24.8%', 'ALL WEAPONS'],
      ['LONGEST STREAK', '17', 'PERSONAL BEST'],
      ['SCORE / MIN', '412', 'LAST 10 MATCHES'],
    ]) {
      const t = el('div', 'mm-stat');
      t.appendChild(el('span', 'mm-stat-k', k));
      t.appendChild(el('b', 'mm-stat-v', v));
      t.appendChild(el('span', 'mm-stat-s', sub));
      stats.appendChild(t);
    }
    p.appendChild(stats);

    const foot = el('div', 'mm-foot');
    foot.appendChild(el('span', 'mm-foot-l', 'BUILD 1.0.0-BLACKOUT · WEBGL2 / ANGLE-METAL'));
    const r = el('span', 'mm-foot-r');
    r.appendChild(el('span', 'mm-pill', 'ONLINE'));
    r.appendChild(el('span', null, 'PING 34 MS'));
    foot.appendChild(r);
    p.appendChild(foot);

    const card = el('div', 'mm-card');
    card.appendChild(el('div', 'mm-card-rank', '54'));
    const ci = el('div', 'mm-card-info');
    ci.appendChild(el('div', 'mm-card-name', 'OPERATOR'));
    ci.appendChild(el('div', 'mm-card-meta', 'K/D 1.84 · W/L 1.32'));
    const bar = el('div', 'mm-card-bar');
    bar.appendChild(el('i', null));
    ci.appendChild(bar);
    card.appendChild(ci);
    p.appendChild(card);

    /* mission brief — fills the right half and gives the frame a second focus */
    const brief = el('div', 'mm-brief');
    brief.appendChild(el('div', 'mm-brief-eyebrow', 'ACTIVE CONTRACT'));
    brief.appendChild(el('div', 'mm-brief-title', 'BLACKOUT / 04'));
    brief.appendChild(el('div', 'mm-brief-line', 'VERDANSK OUTSKIRTS · NIGHT · LIGHT RAIN'));
    const objs = el('div', 'mm-obj');
    const rows = [
      ['done', 'BREACH THE PERIMETER', 'COMPLETE'],
      ['live', 'SECURE SITE A', 'IN PROGRESS'],
      ['todo', 'PLANT THE CHARGE AT SITE B', 'LOCKED'],
      ['todo', 'EXFIL — ROOFTOP LZ', 'LOCKED'],
    ];
    for (const [st, name, status] of rows) {
      const r = el('div', 'mm-obj-row ' + st);
      r.appendChild(el('i', 'mm-obj-pip'));
      r.appendChild(el('span', 'mm-obj-name', name));
      r.appendChild(el('span', 'mm-obj-status', status));
      objs.appendChild(r);
    }
    brief.appendChild(objs);
    const squad = el('div', 'mm-squad');
    for (const [n, role] of [['OPERATOR', 'ASSAULT'], ['HAWKES', 'BREACHER'], ['VARGAS', 'SUPPORT'], ['RUIZ', 'RECON']]) {
      const c = el('div', 'mm-squad-chip' + (n === 'OPERATOR' ? ' you' : ''));
      c.appendChild(el('b', null, n));
      c.appendChild(el('span', null, role));
      squad.appendChild(c);
    }
    brief.appendChild(squad);
    p.appendChild(brief);
    return p;
  }

  _buildPause() {
    const p = el('div', 'menu-panel menu-pause');
    const box = el('div', 'pause-box');
    box.appendChild(el('div', 'pause-eyebrow', 'MISSION PAUSED'));
    box.appendChild(el('div', 'pause-title', 'OPERATION BLACKOUT'));
    box.appendChild(el('div', 'mm-rule'));
    const nav = el('div', 'pause-nav');
    const items = [
      ['RESUME', () => this.close()],
      ['SETTINGS', () => this.open('settings')],
      ['CONTROLS & INFO', () => { this.close(); this.hud?.intro?.show?.(); }],
      ['RESTART CHECKPOINT', () => this.close()],
      ['QUIT TO MENU', () => this.open('main')],
    ];
    items.forEach(([name, fn], i) => {
      const b = el('button', 'pause-item' + (i === 0 ? ' active' : ''), name);
      b.addEventListener('click', fn);
      b.addEventListener('mouseenter', () => {
        nav.querySelectorAll('.pause-item').forEach((n) => n.classList.remove('active'));
        b.classList.add('active');
      });
      nav.appendChild(b);
    });
    box.appendChild(nav);
    p.appendChild(box);
    return p;
  }

  _buildSettings() {
    const p = el('div', 'menu-panel menu-settings');
    const shell = el('div', 'set-shell');

    const head = el('div', 'set-head');
    head.appendChild(el('div', 'set-title', 'SETTINGS'));
    const tabs = el('div', 'set-tabs');
    this._tabBtns = {};
    for (const [id, name] of [['gameplay', 'GAMEPLAY'], ['video', 'VIDEO'], ['post', 'POST'], ['hud', 'HUD']]) {
      const b = el('button', 'set-tab' + (id === this.tab ? ' active' : ''), name);
      b.addEventListener('click', () => this._setTab(id));
      this._tabBtns[id] = b;
      tabs.appendChild(b);
    }
    head.appendChild(tabs);
    const reset = el('button', 'set-back', 'DEFAULTS');
    reset.addEventListener('click', () => this.resetDefaults());
    head.appendChild(reset);
    const back = el('button', 'set-back primary', 'BACK  [ESC]');
    back.addEventListener('click', () => this.open(this.prevMode || 'pause'));
    head.appendChild(back);
    shell.appendChild(head);

    const body = el('div', 'set-body');
    this._pages = {};
    for (const id of ['gameplay', 'video', 'post', 'hud']) {
      const page = el('div', 'set-page');
      this._pages[id] = page;
      body.appendChild(page);
    }
    const side = el('div', 'set-side');
    this._descEl = el('div', 'set-desc');
    this._descEl.innerHTML = '<b>FIELD OF VIEW</b><p>Vertical field of view of the main camera. Higher values widen peripheral vision at the cost of target size.</p>';
    side.appendChild(el('div', 'set-side-title', 'DETAIL'));
    side.appendChild(this._descEl);
    const hints = el('div', 'set-hints');
    for (const [k, v] of [
      ['DRAG / SCROLL', 'ADJUST VALUE'],
      ['CLICK', 'TOGGLE OR SELECT'],
      ['DEFAULTS', 'RESTORE EVERY VALUE'],
      ['ESC', 'BACK'],
    ]) {
      const row = el('div', 'set-hint');
      row.appendChild(el('kbd', null, k));
      row.appendChild(el('span', null, v));
      hints.appendChild(row);
    }
    side.appendChild(hints);
    const meter = el('div', 'set-meter');
    meter.innerHTML = '<span>RENDER</span><b id="set-meter-val">—</b>';
    side.appendChild(meter);
    this._meter = meter.querySelector('b');
    body.appendChild(side);
    shell.appendChild(body);
    p.appendChild(shell);

    /* ------------------------------------------------------------ rows */
    const G = this._pages.gameplay;
    G.appendChild(sectionTitle('AIM & VIEW'));
    this._slider(G, 'fov', 'FIELD OF VIEW', 65, 120, 1, (v) => v.toFixed(0),
      'Vertical field of view of the main camera. Higher values widen peripheral vision at the cost of target size.');
    this._slider(G, 'adsFov', 'ADS FOV SCALE', 0.5, 1.0, 0.01, (v) => v.toFixed(2),
      'How far the field of view narrows when aiming down sights. 1.00 keeps the hip-fire FOV.');
    this._slider(G, 'sens', 'MOUSE SENSITIVITY', 0.0004, 0.0060, 0.0001, (v) => (v * 1000).toFixed(2),
      'Radians of yaw per unit of mouse travel. Shown x1000.');
    this._slider(G, 'adsSens', 'ADS SENSITIVITY', 0.3, 1.2, 0.01, (v) => v.toFixed(2),
      'Multiplier applied to sensitivity while aiming down sights.');
    this._toggle(G, 'invertY', 'INVERT VERTICAL LOOK',
      'Inverts pitch so pushing the mouse forward looks up.');
    G.appendChild(sectionTitle('RETICLE'));
    this._toggle(G, 'crosshairDot', 'CENTRE DOT',
      'Adds a two-pixel dot at the exact centre of the reticle.');
    this._slider(G, 'crosshairThickness', 'BLADE THICKNESS', 1, 4, 1, (v) => v.toFixed(0),
      'Crosshair blade thickness in device pixels. Values are snapped to the pixel grid so the reticle never goes soft.');
    this._slider(G, 'crosshairOpacity', 'RETICLE OPACITY', 0.2, 1, 0.05, (v) => Math.round(v * 100) + '%',
      'Opacity of the hip-fire reticle. It always fades out entirely at full ADS.');

    const V = this._pages.video;
    V.appendChild(sectionTitle('PRESET'));
    this._segment(V, 'quality', 'QUALITY PRESET', ['POTATO', 'LOW', 'MEDIUM', 'HIGH', 'ULTRA'],
      'Drives ctx.render.setQuality(). Controls shadow resolution, post sample counts and LOD bias.');
    this._slider(V, 'renderScale', 'RENDER SCALE', 0.5, 1.0, 0.05, (v) => Math.round(v * 100) + '%',
      'Internal render resolution as a fraction of the output. The HUD always draws at native resolution.');
    this._slider(V, 'fpsLimit', 'FRAME RATE LIMIT', 30, 240, 10, (v) => (v >= 240 ? 'UNCAPPED' : v.toFixed(0)),
      'Caps the presented frame rate. Useful when profiling a single pass without the GPU boosting.');
    V.appendChild(sectionTitle('SHADOWS & GEOMETRY'));
    this._segment(V, 'shadowQuality', 'SHADOW QUALITY', ['OFF', 'LOW', 'HIGH', 'ULTRA'],
      'Cascade count and shadow map resolution. OFF removes the shadow pass entirely.');
    this._slider(V, 'shadowDistance', 'SHADOW DISTANCE', 20, 200, 5, (v) => v.toFixed(0) + ' M',
      'Far plane of the last shadow cascade, in metres. Shorter distances sharpen the near cascades.');
    this._slider(V, 'lodBias', 'LOD BIAS', -1, 1, 0.05, (v) => v.toFixed(2),
      'Negative values hold higher detail meshes for longer. Positive values swap earlier and cost less.');
    V.appendChild(sectionTitle('TEXTURE'));
    this._segment(V, 'aniso', 'ANISOTROPIC FILTER', ['1x', '4x', '8x', '16x'],
      'Anisotropy on albedo and normal maps. Mostly free on Apple silicon; it is what stops floors going to mush at grazing angles.');
    this._segment(V, 'textureQuality', 'TEXTURE RESOLUTION', ['LOW', 'MED', 'HIGH', 'ULTRA'],
      'Resolution tier requested from ctx.materials. Drops the hero 2048 maps first.');
    V.appendChild(sectionTitle('IMAGE'));
    this._slider(V, 'exposure', 'EXPOSURE', 0.35, 2.2, 0.01, (v) => v.toFixed(2) + ' EV',
      'Tone-mapping exposure applied by the post pipeline. Watch the histogram, not the preview.');
    this._slider(V, 'gamma', 'DISPLAY GAMMA', 0.8, 1.4, 0.01, (v) => v.toFixed(2),
      'Output gamma trim. Set it so the darkest step of a black-level ramp is only just visible.');
    this._slider(V, 'sharpen', 'SHARPENING', 0, 1, 0.05, (v) => Math.round(v * 100) + '%',
      'Post-upscale contrast-adaptive sharpening. Above 60% it starts ringing on high-contrast edges.');

    const P = this._pages.post;
    P.appendChild(sectionTitle('POST PIPELINE'));
    for (const [id, name] of this._passList()) {
      this._toggle(P, 'pass_' + id, name.toUpperCase(),
        `Enable or disable the ${name} pass. Toggling is live — use it to isolate what a pass is costing you.`);
    }

    const HU = this._pages.hud;
    HU.appendChild(sectionTitle('ELEMENTS'));
    this._toggle(HU, 'compass', 'COMPASS', 'Top-centre heading strip with objective bearings.');
    this._toggle(HU, 'minimap', 'MINIMAP', 'Top-left rotating minimap with the ortho-baked map layer.');
    this._toggle(HU, 'killfeed', 'KILLFEED', 'Top-right kill notifications.');
    this._toggle(HU, 'worldMarkers', 'WORLD MARKERS', 'Projected objective and teammate markers, clamped to the screen edge when off-screen.');
    HU.appendChild(sectionTitle('COMBAT FEEDBACK'));
    this._toggle(HU, 'hitMarkers', 'HIT MARKERS', 'Four-tick hit confirmation. White body, star headshot, red kill.');
    this._toggle(HU, 'damageIndicators', 'DAMAGE DIRECTION', 'Arced indicators pointing at whoever is shooting you.');
    HU.appendChild(sectionTitle('PRESENTATION'));
    this._slider(HU, 'hudOpacity', 'HUD OPACITY', 0.25, 1, 0.05, (v) => Math.round(v * 100) + '%',
      'Global opacity of the in-game overlay.');
    this._slider(HU, 'hudScale', 'HUD SCALE', 0.8, 1.3, 0.05, (v) => Math.round(v * 100) + '%',
      'Scales every HUD element about its own anchor. Safe areas are respected.');

    this._setTab(this.tab);
    return p;
  }

  /** Restore every control to the value the engine booted with. */
  resetDefaults() {
    const d = this._defaults || {};
    for (const id of this.rows.keys()) {
      if (id in d) this.apply(id, d[id]);
    }
  }

  _setTab(id) {
    this.tab = id;
    for (const [k, b] of Object.entries(this._tabBtns)) b.classList.toggle('active', k === id);
    for (const [k, pg] of Object.entries(this._pages)) pg.classList.toggle('show', k === id);
  }

  /** What a row actually drives, and whether that API exists yet. */
  _binding(id) {
    const ctx = this.ctx;
    const r = this._render();
    if (id.startsWith('pass_')) {
      const p = id.slice(5);
      return { code: `ctx.render.passes.${p}`, live: !!r?.passes };
    }
    const direct = {
      fov: ['ctx.camera.fov', !!ctx?.camera],
      adsFov: ['ctx.config.adsFovScale', true],
      sens: ['ctx.input.sensitivity', !!ctx?.input],
      adsSens: ['ctx.input.adsSensitivityScale', !!ctx?.input],
      invertY: ['ctx.input.invertY', !!ctx?.input],
      quality: ['ctx.render.setQuality()', typeof r?.setQuality === 'function'],
      exposure: ['ctx.render.setExposure()', typeof r?.setExposure === 'function' || !!ctx?.renderer],
      renderScale: ['ctx.render.setRenderScale()', typeof r?.setRenderScale === 'function'],
    };
    if (direct[id]) return { code: direct[id][0], live: direct[id][1] };
    if (FORWARD[id]) {
      const [key, setter] = FORWARD[id];
      return { code: `ctx.render.${setter}()`, live: typeof r?.[setter] === 'function' };
    }
    return { code: 'ctx.hud', live: true };
  }

  _describe(row, title, desc, id) {
    const set = () => {
      if (this._descEl) {
        const b = this._binding(id);
        this._descEl.innerHTML =
          `<b>${title}</b><p>${desc}</p>`
          + `<div class="set-bind"><span>DRIVES</span><code>${b.code}</code>`
          + `<i class="${b.live ? 'live' : 'pending'}">${b.live ? 'LIVE' : 'PENDING'}</i></div>`;
      }
      this.layer.querySelectorAll('.set-row').forEach((n) => n.classList.remove('focus'));
      row.classList.add('focus');
    };
    row.addEventListener('mouseenter', set);
    row.addEventListener('pointerdown', set);
    return set;
  }

  _row(parent, id, name) {
    const row = el('div', 'set-row');
    row.dataset.id = id;
    row.appendChild(el('div', 'set-label', name));
    const ctl = el('div', 'set-ctl');
    row.appendChild(ctl);
    const val = el('div', 'set-val');
    row.appendChild(val);
    parent.appendChild(row);
    return { row, ctl, val };
  }

  _slider(parent, id, name, min, max, step, fmt, desc) {
    const { row, ctl, val } = this._row(parent, id, name);
    const track = el('div', 'set-track');
    const fill = el('div', 'set-fill');
    const knob = el('div', 'set-knob');
    const ticks = el('div', 'set-ticks');
    for (let i = 0; i <= 4; i++) ticks.appendChild(el('i', null));
    track.appendChild(ticks);
    track.appendChild(fill);
    track.appendChild(knob);
    ctl.appendChild(track);

    const focus = this._describe(row, name, desc, id);
    const setFromX = (clientX) => {
      const r = track.getBoundingClientRect();
      let t = (clientX - r.left) / Math.max(1, r.width);
      t = t < 0 ? 0 : t > 1 ? 1 : t;
      let v = min + t * (max - min);
      v = Math.round(v / step) * step;
      v = Math.min(max, Math.max(min, v));
      this.apply(id, v);
    };
    let dragging = false;
    track.addEventListener('pointerdown', (e) => {
      dragging = true; focus();
      track.setPointerCapture?.(e.pointerId);
      setFromX(e.clientX);
    });
    track.addEventListener('pointermove', (e) => { if (dragging) setFromX(e.clientX); });
    const stop = () => { dragging = false; };
    track.addEventListener('pointerup', stop);
    track.addEventListener('pointercancel', stop);
    row.addEventListener('wheel', (e) => {
      e.preventDefault();
      const v = Math.min(max, Math.max(min, this.values[id] - Math.sign(e.deltaY) * step));
      this.apply(id, v);
    }, { passive: false });

    this.rows.set(id, { kind: 'slider', min, max, fmt, fill, knob, val });
    this._syncRow(id);
  }

  _toggle(parent, id, name, desc) {
    const { row, ctl, val } = this._row(parent, id, name);
    const t = el('button', 'set-switch');
    t.appendChild(el('i', null));
    ctl.appendChild(t);
    this._describe(row, name, desc, id);
    t.addEventListener('click', () => this.apply(id, !this.values[id]));
    this.rows.set(id, { kind: 'toggle', node: t, val });
    this._syncRow(id);
  }

  _segment(parent, id, name, options, desc) {
    const { row, ctl, val } = this._row(parent, id, name);
    const seg = el('div', 'set-seg');
    const btns = options.map((o, i) => {
      const b = el('button', null, o);
      b.addEventListener('click', () => this.apply(id, i));
      seg.appendChild(b);
      return b;
    });
    ctl.appendChild(seg);
    this._describe(row, name, desc, id);
    this.rows.set(id, { kind: 'seg', btns, options, val });
    this._syncRow(id);
  }

  _syncRow(id) {
    const r = this.rows.get(id);
    if (!r) return;
    const v = this.values[id];
    if (r.kind === 'slider') {
      const t = (v - r.min) / (r.max - r.min);
      r.fill.style.transform = `scaleX(${Math.max(0, Math.min(1, t))})`;
      r.knob.style.transform = `translateX(${(Math.max(0, Math.min(1, t)) * 100).toFixed(3)}%)`;
      r.val.textContent = r.fmt(v);
    } else if (r.kind === 'toggle') {
      r.node.classList.toggle('on', !!v);
      r.val.textContent = v ? 'ON' : 'OFF';
    } else if (r.kind === 'seg') {
      r.btns.forEach((b, i) => b.classList.toggle('active', i === v));
      r.val.textContent = r.options[v] || '';
    }
  }

  _sync() {
    for (const id of this.rows.keys()) this._syncRow(id);
    if (this._meter) {
      const s = window.__GAME__?.stats?.();
      this._meter.textContent = s ? `${s.calls} CALLS · ${(s.triangles / 1000).toFixed(0)}K TRIS` : '—';
    }
  }

  /** Called by HUD each frame while the settings panel is up (cheap). */
  tick() {
    if (this.mode === 'settings' && this._meter) {
      const s = window.__GAME__?.stats?.();
      if (s) this._meter.textContent = `${s.calls} CALLS · ${(s.triangles / 1000).toFixed(0)}K TRIS`;
    }
  }
}

/* ------------------------------------------------------------- helpers */

function el(tag, cls, text) {
  const n = document.createElement(tag);
  if (cls) n.className = cls;
  if (text != null) n.textContent = text;
  return n;
}

function sectionTitle(t) {
  const n = el('div', 'set-section');
  n.appendChild(el('span', null, t));
  n.appendChild(el('i', null));
  return n;
}

function pretty(k) {
  return String(k)
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\bpass\b/i, '')
    .trim()
    .toUpperCase();
}
